/**
 * WEB-927 атом 3: РЕНДЕР осей раскладки экрана — стили из посчитанных осей.
 *
 * Оси считает ТОЛЬКО resolveScreenLayout (экран > стиль-пак > легаси), а этот
 * модуль — единственное место, где они превращаются в стили. Один модуль на
 * три поверхности (квиз, пейволл, превью конструктора), иначе «в превью одно,
 * в проде другое».
 *
 * Разделение труда внутри экрана:
 *   • ХРОМ (прогресс, лого, «назад», подпись раздела) остаётся вверху при
 *     любой оси. Он не получает авто-отступов и не сжимается.
 *   • КОЛОНКА КОНТЕНТА (поток блоков + CTA) — единственное, что двигают оси.
 *
 * Почему авто-отступы, а не justify-content/align-items: авто-отступ съедает
 * только ПОЛОЖИТЕЛЬНОЕ свободное место, а когда места не хватает —
 * схлопывается в 0, контент остаётся сверху и нормально скроллится.
 * `justify-content: center` в той же ситуации выносит верх блока за начало
 * скролл-контейнера, и первые блоки становятся недоступны. Смысл фичи —
 * распределить ПУСТОТУ, а не сломать длинный экран.
 *
 * Горизонталь — положение самой колонки, а не text-align внутри блоков:
 * ширина колонки остаётся паковой (--content-max-w). Без пака переменной нет,
 * фолбэк 100% = колонка во всю доступную ширину, свободного места нет, ось
 * визуально ничего не двигает (та же логика «только при свободном месте»).
 */
import type { CSSProperties } from 'react';
import type { ScreenAlignX, ScreenAlignY } from '@/themes/stylePack.types';

export interface ScreenAxes {
  alignY: ScreenAlignY;
  alignX: ScreenAlignX;
  /**
   * На экране гибкий отступ (flexSpacer.tsx): экран на всю высоту, колонка
   * контента — flex-колонка до низа; вертикальная ось не действует — место
   * распределяет отступ.
   */
  fill?: boolean;
}

/** Ширина читаемой колонки из стиль-пака; без пака — вся доступная ширина. */
export const CONTENT_COLUMN_MAX_WIDTH = 'var(--content-max-w, 100%)';

/**
 * Нужна ли раскладка вообще. Легаси-оси (top + center) = «как рисует прод
 * сегодня»: ни обёртки, ни единого нового свойства.
 */
export function screenAlignsContent(axes: ScreenAxes): boolean {
  return axes.fill === true || axes.alignY !== 'top' || axes.alignX !== 'center';
}

/**
 * Корень экрана. Вертикальная ось требует, чтобы корень занял всю доступную
 * высоту (иначе свободного места нет и распределять нечего) и стал колонкой —
 * тогда авто-отступы колонки контента работают по главной оси.
 *
 * min-height НЕ задаём: у flex-элемента дефолтный min-height:auto не даёт ему
 * сжаться меньше контента — это и есть страховка «длинный экран не обрежется».
 */
export function screenRootLayoutStyle(axes: ScreenAxes): CSSProperties {
  if (axes.alignY === 'top' && !axes.fill) return {};
  return { display: 'flex', flexDirection: 'column', flex: 1 };
}

/**
 * Хром экрана. Авто-отступов не получает НИКОГДА — он остаётся первым в
 * колонке, то есть вверху экрана, при любой оси.
 */
export function screenChromeLayoutStyle(axes: ScreenAxes): CSSProperties {
  return {
    // В flex-колонке хром не должен ужиматься под давлением длинного контента.
    ...(axes.alignY === 'top' && !axes.fill ? {} : { flexShrink: 0 }),
    // Горизонталь двигает только контент: хром держит свою колонку по центру,
    // как её держал предок до снятия ограничения ширины.
    ...(axes.alignX === 'center'
      ? {}
      : {
          width: '100%',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH,
          marginLeft: 'auto',
          marginRight: 'auto',
        }),
  };
}

/** Колонка контента — единственный узел, который двигают оси. */
export function screenContentColumnStyle(axes: ScreenAxes): CSSProperties {
  return {
    // Гибкий отступ: колонка растёт до низа экрана, но не сжимается —
    // длинный экран скроллится как раньше. Авто-отступы оси здесь не нужны.
    ...(axes.fill
      ? {
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: 'auto',
        }
      : {}),
    ...(!axes.fill && axes.alignY === 'center'
      ? { marginTop: 'auto', marginBottom: 'auto' }
      : {}),
    ...(!axes.fill && axes.alignY === 'bottom' ? { marginTop: 'auto' } : {}),
    ...(axes.alignX === 'center'
      ? {}
      : {
          width: '100%',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH,
          marginLeft: axes.alignX === 'right' ? 'auto' : 0,
          marginRight: axes.alignX === 'left' ? 'auto' : 0,
        }),
  };
}
